import React, { useState, useEffect } from 'react';
import { UserPlus, Trash2, Edit2, Save, X, Lock, Cloud, Download, LogIn, LogOut as LogOutIcon } from 'lucide-react';
import '../styles/Settings.css';
import { userAPI } from '../utils/api';

function Settings() {
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'staff'
  });

  const [passwordData, setPasswordData] = useState({
    userId: '',
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [backupLoading, setBackupLoading] = useState(false);
  const [googleAuthenticated, setGoogleAuthenticated] = useState(false);
  const [googleUserEmail, setGoogleUserEmail] = useState(null);
  const [showGoogleLogin, setShowGoogleLogin] = useState(false);

  const roles = ['admin', 'staff', 'manager'];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll();
      setUsers(response.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load users: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      setError('Username and password are required');
      return;
    }

    try {
      const response = await userAPI.create({
        username: formData.username,
        password: formData.password,
        role: formData.role
      });

      setUsers([...users, response.data]);
      setFormData({ username: '', password: '', role: 'staff' });
      setShowAddUser(false);
      setSuccessMsg('User added successfully!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Failed to add user: ' + err.message);
    }
  };

  const handleUpdateUser = async (id) => {
    try {
      const response = await userAPI.update(id, {
        role: formData.role
      });

      setUsers(users.map(u => u.id === id ? response.data : u));
      setEditingId(null);
      setSuccessMsg('User updated successfully!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Failed to update user: ' + err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await userAPI.delete(id);
      setUsers(users.filter(u => u.id !== id));
      setSuccessMsg('User deleted successfully!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Failed to delete user: ' + err.message);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.userId || !passwordData.oldPassword || !passwordData.newPassword) {
      setError('All fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      await userAPI.changePassword(passwordData.userId, {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordData({ userId: '', oldPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordChange(false);
      setSuccessMsg('Password changed successfully!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Failed to change password: ' + err.message);
    }
  };

  const handleEditClick = (user) => {
    setEditingId(user.id);
    setFormData({ username: user.username, password: '', role: user.role });
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowAddUser(false);
    setShowPasswordChange(false);
    setFormData({ username: '', password: '', role: 'staff' });
    setPasswordData({ userId: '', oldPassword: '', newPassword: '', confirmPassword: '' });
    setError(null);
  };

  const handleGoogleLogin = async () => {
    try {
      setBackupLoading(true);
      
      // For testing/demo: directly authenticate without real OAuth
      // In production, you would use the real Google Sign-In flow
      const mockGoogleAuth = {
        email: prompt('Enter your Google email (or leave empty for demo):') || 'test@gmail.com',
        name: 'Google User'
      };

      if (!mockGoogleAuth.email) {
        throw new Error('Email is required');
      }

      // Send to backend for storage
      const response = await fetch('/api/auth/google/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: mockGoogleAuth.email,
          name: mockGoogleAuth.name,
          isTestMode: true
        })
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const data = await response.json();
      setGoogleAuthenticated(true);
      setGoogleUserEmail(data.email || mockGoogleAuth.email);
      setSuccessMsg('Successfully connected to Google! (Demo Mode)');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      if (err.message === 'Email is required') {
        setError('Please enter an email to continue');
      } else {
        setError('Failed to connect to Google: ' + err.message);
      }
    } finally {
      setBackupLoading(false);
    }
  };

  const handleGoogleCallback = async (response) => {
    // This is kept for future real OAuth implementation
    try {
      setBackupLoading(true);
      
      const backendResponse = await fetch('/api/auth/google/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idToken: response.credential
        })
      });

      if (!backendResponse.ok) {
        throw new Error('Backend authentication failed');
      }

      const data = await backendResponse.json();
      setGoogleAuthenticated(true);
      setGoogleUserEmail(data.email || response.credential.email || 'Google Account');
      setSuccessMsg('Successfully connected to Google!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Failed to authenticate with Google: ' + err.message);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleGoogleLogout = async () => {
    try {
      await fetch('/api/auth/google/logout', { method: 'POST' });
      setGoogleAuthenticated(false);
      setGoogleUserEmail(null);
      setSuccessMsg('Google account disconnected');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Failed to logout: ' + err.message);
    }
  };

  // Check Google auth status on mount
  useEffect(() => {
    const checkGoogleAuth = async () => {
      try {
        const response = await fetch('/api/auth/google/status');
        if (response.ok) {
          const data = await response.json();
          if (data.authenticated) {
            setGoogleAuthenticated(true);
            setGoogleUserEmail(data.email);
          }
        }
      } catch (err) {
        console.log('Not authenticated with Google');
      }
    };

    checkGoogleAuth();
  }, []);

  const handleBackupToGoogleCloud = async () => {
    if (!window.confirm('This will backup your entire database to Google Cloud. Continue?')) {
      return;
    }

    try {
      setBackupLoading(true);
      const response = await fetch('/api/backup/google-cloud', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Backup failed');
      }

      const data = await response.json();
      setSuccessMsg(`Backup successful! Backup ID: ${data.backupId}`);
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err) {
      setError('Failed to backup data: ' + err.message);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      setBackupLoading(true);
      const response = await fetch('/api/backup/download', {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing-backup-${new Date().toISOString().slice(0, 10)}.db`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccessMsg('Backup downloaded successfully!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Failed to download backup: ' + err.message);
    } finally {
      setBackupLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings & User Management</h1>
        <button className="btn-primary" onClick={() => setShowAddUser(true)}>
          <UserPlus size={20} />
          Add New User
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      {/* Add User Form */}
      {showAddUser && (
        <div className="form-card">
          <h2>Add New User</h2>
          <form onSubmit={handleAddUser}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Enter username"
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
              />
            </div>

            <div className="form-group">
              <label>Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-success">Add User</button>
              <button type="button" className="btn-secondary" onClick={handleCancel}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Change Password Form */}
      {showPasswordChange && (
        <div className="form-card">
          <h2>Change Password</h2>
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label>Select User</label>
              <select
                value={passwordData.userId}
                onChange={(e) => setPasswordData({ ...passwordData, userId: e.target.value })}
              >
                <option value="">Select a user</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.username} ({user.role})</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={passwordData.oldPassword}
                onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                placeholder="Enter current password"
              />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Enter new password"
              />
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-success">Change Password</button>
              <button type="button" className="btn-secondary" onClick={handleCancel}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      <div className="users-section">
        <h2>User Management</h2>
        <div className="table-actions">
          <button 
            className="btn-secondary" 
            onClick={() => setShowPasswordChange(true)}
          >
            <Lock size={16} />
            Change Password
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="no-data">No users found</div>
        ) : (
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>
                      {editingId === user.id ? (
                        <select
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="role-select"
                        >
                          {roles.map(role => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`role-badge role-${user.role}`}>{user.role}</span>
                      )}
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString('en-IN')}</td>
                    <td className="actions">
                      {editingId === user.id ? (
                        <>
                          <button
                            className="btn-icon btn-success"
                            onClick={() => handleUpdateUser(user.id)}
                            title="Save"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            className="btn-icon btn-secondary"
                            onClick={handleCancel}
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn-icon btn-info"
                            onClick={() => handleEditClick(user)}
                            title="Edit Role"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="btn-icon btn-danger"
                            onClick={() => handleDeleteUser(user.id)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Role Information */}
      <div className="info-section">
        <h3>User Roles & Permissions</h3>
        <div className="roles-grid">
          <div className="role-card">
            <h4>Admin</h4>
            <p>Full access to all features including user management, settings, and all reports.</p>
          </div>
          <div className="role-card">
            <h4>Manager</h4>
            <p>Access to invoicing, customer management, inventory, and basic reports.</p>
          </div>
          <div className="role-card">
            <h4>Staff</h4>
            <p>Limited access to invoicing and customer information only.</p>
          </div>
        </div>
      </div>

      {/* Data Backup & Recovery */}
      <div className="info-section">
        <h3>Data Backup & Recovery</h3>
        <p className="backup-description">
          Backup your entire database to keep your data safe. Choose between local download or cloud storage.
        </p>

        {/* Google Authentication */}
        <div className="google-auth-section">
          <h4>Google Cloud Authentication</h4>
          {googleAuthenticated ? (
            <div className="auth-status authenticated">
              <div className="auth-info">
                <span className="auth-status-label">✓ Connected</span>
                <span className="auth-email">{googleUserEmail}</span>
              </div>
              <button 
                className="auth-btn logout"
                onClick={handleGoogleLogout}
              >
                <LogOutIcon size={16} />
                Disconnect Google
              </button>
            </div>
          ) : (
            <div>
              <button 
                className="auth-btn google"
                onClick={handleGoogleLogin}
                disabled={backupLoading}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Connect to Google
              </button>
              <p style={{ fontSize: '12px', marginTop: '10px', color: '#666' }}>
                Click above to authenticate with Google. Enter your email when prompted. 
                For production, follow the GOOGLE_OAUTH_SETUP.md guide.
              </p>
            </div>
          )}        </div>

        {/* Backup Buttons */}
        <div className="backup-buttons">
          <button 
            className="backup-btn google-cloud"
            onClick={handleBackupToGoogleCloud}
            disabled={backupLoading || !googleAuthenticated}
            title={!googleAuthenticated ? "Please authenticate with Google first" : ""}
          >
            <Cloud size={18} />
            <span>{backupLoading ? 'Backing up...' : 'Backup to Google Cloud'}</span>
          </button>
          <button 
            className="backup-btn local"
            onClick={handleDownloadBackup}
            disabled={backupLoading}
          >
            <Download size={18} />
            <span>{backupLoading ? 'Downloading...' : 'Download Local Backup'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;
